import { useEffect, useState } from "react";
import { Check, Crown, Star, Gift, Smartphone } from "lucide-react";
import { Link } from "react-router-dom";
import { endpoints } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SubscribeDialog } from "@/components/SubscribeDialog";
import { planPrice, pickCurrency } from "@/lib/currency";

const PERIODS = ["daily", "weekly", "monthly", "yearly"];
const LABEL = { daily: "1 day", weekly: "7 days", monthly: "30 days", yearly: "365 days" };
const UNIT = { daily: "day", weekly: "week", monthly: "month", yearly: "year" };
const TIERS = {
  basic: { title: "Basic", perks: ["Ad-free live radio", "Highlights & free shows", "Listen worldwide"], color: "#E11D2A" },
  premium: { title: "Premium", perks: ["Everything in Basic", "HD radio + Live TV", "Members-only shows & VOD", "Early access to events"], color: "#2563EB" },
};

const saving = (plans, p) => {
  const d = plans.find((x) => x.tier === p.tier && x.period === "daily");
  if (!d || p.period === "daily") return 0;
  const pct = Math.round((1 - p.amount / (d.amount * p.days)) * 100);
  return pct >= 5 ? pct : 0;
};

const OfferRow = ({ plan, plans, price, color, onPick }) => {
  const s = saving(plans, plan);
  return (
    <div className="flex items-center gap-3 py-3 pr-12 sm:pr-0" data-testid={`plan-card-${plan.id}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-white font-semibold text-sm capitalize">{plan.period}</span>
          <span className="text-[11px] text-slate-500">{LABEL[plan.period]}</span>
          {s > 0 && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400" data-testid={`plan-save-${plan.id}`}>Save {s}%</span>}
        </div>
        <div className="text-white text-lg sm:text-xl font-extrabold leading-tight" data-testid={`plan-price-${plan.id}`}>
          {price}<span className="text-xs text-slate-400 font-medium"> /{UNIT[plan.period]}</span>
        </div>
        {plan.rwfOnly && <div className="text-[10px] text-amber-200 flex items-center gap-1" data-testid={`plan-momo-only-${plan.id}`}><Smartphone className="w-3 h-3" /> MTN Mobile Money</div>}
      </div>
      <button onClick={() => onPick(plan)} data-testid={`subscribe-btn-${plan.id}`}
        className="h-10 px-4 sm:px-5 shrink-0 rounded-full text-white text-sm font-bold transition-opacity hover:opacity-90" style={{ background: color }}>
        Choose
      </button>
    </div>
  );
};

const TierCard = ({ tier, plans, priceOf, onPick, current }) => {
  const t = TIERS[tier];
  const list = PERIODS.map((p) => plans.find((x) => x.tier === tier && x.period === p)).filter(Boolean);
  if (!list.length) return null;
  const premium = tier === "premium";
  return (
    <div data-testid={`tier-${tier}`} className={`relative rounded-2xl p-4 sm:p-6 border ${premium ? "border-[#2563EB]/50 bg-gradient-to-br from-[#0B1220] to-[#0A0C10]" : "border-[#273042] bg-[#121620]"}`}>
      {premium && <span className="absolute -top-3 left-4 px-3 py-1 rounded-full bg-[#2563EB] text-white text-[10px] font-extrabold uppercase flex items-center gap-1"><Star className="w-3 h-3" /> Most popular</span>}
      <div className="flex items-center justify-between gap-2">
        <div className="text-white font-bold text-xl flex items-center gap-2">{premium && <Crown className="w-5 h-5 text-[#2563EB]" />}{t.title}</div>
        {current && <span className="text-[11px] font-bold text-emerald-400" data-testid={`tier-current-${tier}`}>Your plan ✓</span>}
      </div>
      <ul className="mt-3 grid gap-1.5">
        {t.perks.map((p) => <li key={p} className="flex items-start gap-2 text-xs sm:text-sm text-slate-300"><Check className="w-4 h-4 shrink-0" style={{ color: t.color }} />{p}</li>)}
      </ul>
      <div className="mt-3 divide-y divide-[#273042] border-t border-[#273042]">
        {list.map((p) => <OfferRow key={p.id} plan={p} plans={plans} price={p.rwfOnly ? `${Number(p.amount).toLocaleString()} RWF` : priceOf(p)} color={t.color} onPick={onPick} />)}
      </div>
    </div>
  );
};

export default function Pricing() {
  const { isAuthed, isSubscribed, user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [cur, setCur] = useState(null);
  const [sub, setSub] = useState({ open: false, plan: null });

  useEffect(() => { endpoints.plans().then((r) => setPlans(r.data || [])).catch(() => {}); }, []);
  useEffect(() => {
    endpoints.billingCurrencies().then((r) => {
      const list = r.data?.currencies || [];
      setCurrencies(list);
      setCur(pickCurrency(list, user));
    }).catch(() => {});
  }, [user]);

  const curCode = cur?.code || "RWF";
  const priceOf = (p) => planPrice(p, cur || "RWF");
  const choose = (plan) => {
    if (!isAuthed) { window.dispatchEvent(new Event("open-auth")); return; }
    setSub({ open: true, plan });
  };

  return (
    <div className="px-3 sm:px-6 lg:px-8 max-w-5xl mx-auto py-8 sm:py-12 pb-28">
      <div className="text-center">
        <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#2563EB]/15 border border-[#2563EB]/30 text-[#60A5FA] text-xs font-bold uppercase tracking-wider mb-3"><Crown className="w-4 h-4" /> Membership</div>
        <h1 className="text-3xl sm:text-5xl font-extrabold text-white tracking-tight">All our plans</h1>
        <p className="text-slate-400 mt-2 text-sm sm:text-base max-w-lg mx-auto">Pay by the day, week, month or year. Pick any offer below.</p>
        {isSubscribed && <div className="mt-3 inline-block text-sm text-[#22C55E] font-semibold" data-testid="pricing-active">You're an active {user?.tier} member ✓</div>}
        <div><Link to="/utilities" data-testid="pricing-utilities-promo" className="mt-4 inline-flex items-center gap-2 rounded-full border border-[#E11D2A]/50 bg-[#E11D2A]/10 px-4 py-2 text-xs sm:text-sm text-white font-semibold hover:bg-[#E11D2A]/20 transition-colors">
          <Gift className="w-4 h-4 text-[#E11D2A] shrink-0" /> Buy airtime or electricity → 48 hours FREE</Link></div>
      </div>

      {currencies.length > 1 && (
        <div className="flex flex-wrap items-center justify-center gap-2 mt-6">
          <span className="text-xs text-slate-400">Monthly & yearly prices in</span>
          <div className="inline-flex rounded-full bg-[#121620] border border-[#273042] p-1 flex-wrap" data-testid="pricing-currency-toggle">
            {currencies.map((c) => (
              <button key={c.code} onClick={() => setCur(c)} data-testid={`pricing-currency-${c.code}`}
                className={`px-4 py-1.5 rounded-full text-xs sm:text-sm font-semibold transition-colors ${curCode === c.code ? "bg-[#2563EB] text-white" : "text-slate-300"}`}>
                {c.symbol !== c.code ? `${c.symbol} ` : ""}{c.code}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-5 sm:gap-6 mt-8" data-testid="pricing-all-plans">
        {["basic", "premium"].map((t) => <TierCard key={t} tier={t} plans={plans} priceOf={priceOf} onPick={choose} current={isSubscribed && user?.tier === t} />)}
      </div>

      <SubscribeDialog plan={sub.plan} currency={sub.plan?.rwfOnly ? "RWF" : curCode} currencies={currencies} open={sub.open} onClose={() => setSub({ open: false, plan: null })} />
    </div>
  );
}
